import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import {
  SalesEntityAvatar,
  SalesPageHeader,
  SalesSectionCard,
  SALES,
  salesPrimaryButtonSx,
} from '../../components/sales/salesUi';

const displayValue = (v) => (v == null || v === '' ? '—' : String(v));

const SalesProfile = () => {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();

  const handleChangePassword = async () => {
    setMessage('');
    setError('');
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await axios.post('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });
      setMessage('Password updated.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <SalesPageHeader
        title="Profile"
        subtitle="Your account details and password"
      />

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
        <SalesEntityAvatar name={fullName || user?.email || 'Rep'} size={56} />
        <Box>
          <Typography sx={{ fontWeight: 700, color: SALES.ink, fontSize: '1.1rem' }}>
            {displayValue(fullName)}
          </Typography>
          <Typography variant="body2" sx={{ color: SALES.muted }}>
            Outside Sales Rep
          </Typography>
        </Box>
      </Stack>

      <SalesSectionCard title="Account">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" sx={{ color: SALES.muted, fontWeight: 600 }}>Name</Typography>
            <Typography>{displayValue(fullName)}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" sx={{ color: SALES.muted, fontWeight: 600 }}>Email</Typography>
            <Typography>{displayValue(user?.email)}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" sx={{ color: SALES.muted, fontWeight: 600 }}>Role</Typography>
            <Typography>Outside Sales Rep</Typography>
          </Grid>
        </Grid>
      </SalesSectionCard>

      <SalesSectionCard title="Change Password" subtitle="Use at least 6 characters">
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small" type="password" label="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small" type="password" label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small" type="password" label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleChangePassword}
              disabled={saving}
              sx={salesPrimaryButtonSx}
            >
              Update Password
            </Button>
          </Grid>
        </Grid>
      </SalesSectionCard>
    </Box>
  );
};

export default SalesProfile;
